import React from 'react';
import { View, Text } from 'react-native';

import { SmallLoading } from '@components/loading';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { JobCardDaysBadgeProps } from '@v2/pages/planboard/types';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import usePriorityAdjustment from '@v2/pages/planboard/hooks/usePriorityAdjustment';
import { TourTarget } from '@components/tours';
import { getBoxShadowStyle } from '@styles/shadow';

const JobCardDaysBadge: React.FC<JobCardDaysBadgeProps> = ({
  scheduledStartDate,
  expectedStartOn,
  // showPinIcon = false, // TODO: Enable when backend adds isPinned field
  isPending = false,
  scale = initialScale,
  font = initialFont,
  C,
  hasPriorityOverride,
  scheduledStartDateOverride,
  onBadgePress,
  isHighlighted = false,
}) => {
  const {
    hasPriorityOverride: hasOverride,
    isPriorityIncreased,
    isPriorityDecreased,
    daysDifference,
    daysDisplayText,
    isBehind,
  } = usePriorityAdjustment({
    scheduledStartDate,
    scheduledStartDateOverride,
    expectedStartOn,
    hasPriorityOverride,
  });

  // Get the color key for the badge
  const badgeColorKey = daysDifference === null
    ? 'Label/Light/Tertiary'
    : isBehind
      ? 'Chart/Light/Warning'
      : 'System/Light/Blue';

  // Determine if we should show arrow icon
  const showArrowUp = hasOverride && isPriorityIncreased;
  const showArrowDown = hasOverride && isPriorityDecreased;
  const hasExtraIcons = showArrowUp || showArrowDown; // TODO: Add showPinIcon when backend adds isPinned field

  // Get shadow style when highlighted
  const highlightShadowStyle = isHighlighted ? getBoxShadowStyle({ type: 'Narrow Strong', C }) : undefined;

  const renderBadgeContent = (isHovered = false) => (
    <View
      style={[
        {
          backgroundColor: C(badgeColorKey),
          // Darken on hover by reducing opacity slightly
          opacity: isHovered ? 0.85 : 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: scale(hasExtraIcons ? 20 : 12),
          paddingVertical: scale(2),
          paddingHorizontal: hasExtraIcons ? scale(7) : scale(8),
          minWidth: hasExtraIcons ? undefined : scale(24),
          gap: scale(2),
        },
        highlightShadowStyle,
      ]}
    >
      {isPending ? (
        <SmallLoading color={C('Label/Light/AlwaysLight Primary')} />
      ) : (
        <>
          <Text style={[font['Button 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>
            {daysDisplayText}
          </Text>
          {showArrowUp && (
            <Svg
              icon="arrowVertical"
              width={scale(7)}
              height={scale(8)}
              color="Label/Light/AlwaysLight Primary"
            />
          )}
          {showArrowDown && (
            <View style={{ transform: [{ rotate: '180deg' }] }}>
              <Svg
                icon="arrowVertical"
                width={scale(7)}
                height={scale(8)}
                color="Label/Light/AlwaysLight Primary"
              />
            </View>
          )}
          {/* TODO: Enable pin icon when backend adds isPinned field
          {showPinIcon && (
            <Svg
              icon="pin"
              width={scale(7)}
              height={scale(10)}
              color="Label/Light/AlwaysLight Primary"
            />
          )}
          */}
        </>
      )}
    </View>
  );

  // If onBadgePress is provided, wrap in TouchableElement with hover effect
  if (onBadgePress) {
    return (
      <TourTarget tourId="planboard-card-days-badge">
        <TouchableElement onPress={onBadgePress}>
          {({ hovered }) => renderBadgeContent(hovered)}
        </TouchableElement>
      </TourTarget>
    );
  }

  return renderBadgeContent();
};

export default JobCardDaysBadge;
